/**
 * NsectionB14Page
 * B.14.事前參數說明（A6.4-FORM-METH-002 Section 14. Data and parameters not monitored）
 * DataTable + Modal（B.13 參數下拉輔助，para_type=249/null）
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import nsectionB14Service, { NsectionB14Detail, ParameterItem } from '../services/nsectionB14Service';
import nsectionB13Service, { ParameterSummaryItem } from '../services/nsectionB13Service';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const COMMON_UNITS = [
  { label: 'tCO₂e', latex: '\\text{tCO}_2\\text{e}' }, { label: 'MWh', latex: '\\text{MWh}' },
  { label: 'GWh', latex: '\\text{GWh}' }, { label: 'kWh', latex: '\\text{kWh}' },
  { label: 'GJ', latex: '\\text{GJ}' }, { label: 'kg', latex: '\\text{kg}' },
  { label: 't', latex: '\\text{t}' }, { label: '%', latex: '\\%' },
  { label: 'tCO₂e/MWh', latex: '\\text{tCO}_2\\text{e/MWh}' }, { label: 'tCO₂e/GJ', latex: '\\text{tCO}_2\\text{e/GJ}' },
];

const EMPTY_PARAM = (): ParameterItem => ({
  id: crypto.randomUUID(), data_parameter: '', description: '', data_unit: '', equations_referred: '',
  purpose_baseline: false, purpose_project: false, purpose_leakage: false,
  values_applied: '', source: null, measurement_methods: '', treatment_of_uncertainty: '', additional_comments: '',
  b13_ref: null,
});

const NsectionB14Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh-TW';
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProposalId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  const [data, setData] = useState<NsectionB14Detail | null>(null);
  const [parameters, setParameters] = useState<ParameterItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // B.13 可用參數
  const [b13Params, setB13Params] = useState<ParameterSummaryItem[]>([]);
  const [availableParams, setAvailableParams] = useState<ParameterSummaryItem[]>([]);

  // Modal
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [modalParam, setModalParam] = useState<ParameterItem>(EMPTY_PARAM());

  useEffect(() => { if (!hasInitialized.current && !permissionLoading) { validateSession('NsectionB14Page'); hasInitialized.current = true; } }, [permissionLoading]);

  // 載入 B.13 參數總覽（篩選 para_type=249/null）
  const loadB13Params = async () => {
    if (!selectedProposalId) return;
    try {
      const all = await nsectionB13Service.getParametersSummary(selectedProposalId);
      const typeFiltered = all.filter(p => p.para_type === 249 || p.para_type === null);
      // 以 symbol 去重，保留第一筆
      const seen = new Set<string>();
      const filtered = typeFiltered.filter(p => {
        if (seen.has(p.symbol)) return false;
        seen.add(p.symbol);
        return true;
      });
      setB13Params(filtered);
    } catch { setB13Params([]); }
  };

  useEffect(() => {
    if (!selectedProposalId) { setData(null); setParameters([]); setB13Params([]); return; }
    const load = async () => {
      try {
        setLoading(true); setError(null);
        const r = await nsectionB14Service.getByProposal(selectedProposalId);
        if (r) { setData(r); setParameters(r.parameters || []); }
        else { setData(null); setParameters([]); }
      } catch (err: any) { if (err.response?.status !== 404) setError(err.response?.data?.detail || (isZh ? '載入失敗' : 'Load failed')); }
      finally { setLoading(false); }
    };
    load();
    loadB13Params();
  }, [selectedProposalId, isZh]);

  // 計算可用（未建立）的 B.13 參數
  useEffect(() => {
    const built = new Set(parameters.map(p => p.data_parameter));
    setAvailableParams(b13Params.filter(p => !built.has(p.symbol)));
  }, [b13Params, parameters]);

  const builtCount = b13Params.length - availableParams.length;
  const totalCount = b13Params.length;

  // Modal
  const openModal = (index?: number) => {
    if (index !== undefined) {
      setEditingIndex(index);
      setModalParam({ ...parameters[index] });
    } else {
      setEditingIndex(null);
      setModalParam(EMPTY_PARAM());
    }
    setShowModal(true);
  };

  const handleB13Select = (symbol: string) => {
    const p = b13Params.find(x => x.symbol === symbol);
    if (!p) return;
    setModalParam(prev => ({
      ...prev,
      data_parameter: p.symbol,
      data_unit: p.unit || '',
      equations_referred: `${p.source_section} ${p.source_label}`,
      b13_ref: { source_section: p.source_section, source_label: p.source_label, symbol: p.symbol, para_type: p.para_type, equation_latex: p.equation_latex },
    }));
  };

  const handleSaveParam = (continueAdd: boolean) => {
    if (!modalParam.data_parameter.trim()) { toast.warning(isZh ? '請輸入參數名稱' : 'Please enter parameter name'); return; }
    const updated = [...parameters];
    if (editingIndex !== null) {
      updated[editingIndex] = modalParam;
    } else {
      updated.push(modalParam);
    }
    setParameters(updated);
    if (continueAdd) {
      setModalParam(EMPTY_PARAM());
      setEditingIndex(null);
    } else {
      setShowModal(false);
    }
  };

  const handleDeleteParam = async (index: number) => {
    const ok = await toast.confirm({
      title: isZh ? '刪除確認' : 'Delete Confirmation',
      message: isZh ? '確定刪除此參數？' : 'Delete this parameter?',
      confirmText: isZh ? '刪除' : 'Delete',
      severity: 'error',
    });
    if (!ok) return;
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    if (!selectedProposalId) return;
    try {
      setLoading(true);
      const r = await nsectionB14Service.update(selectedProposalId, { parameters });
      setData(r); toast.success(isZh ? '儲存成功' : 'Saved successfully');
    } catch (err: any) { toast.error(err.response?.data?.detail || (isZh ? '儲存失敗' : 'Failed')); }
    finally { setLoading(false); }
  };

  if (permissionLoading) return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  if (!hasPermission('nsection_b14', 'read')) return <div className="page-container"><div className="error-message">{t('common.noPermission')}</div></div>;
  const canEdit = hasPermission('nsection_b14', 'update');

  return (
    <div className="page-container">
      <div className="page-header"><FunctionPageHeader funcCode="nsection_b14" /></div>
      {error && <div className="error-message" style={{ marginBottom: 16 }}>{error}</div>}

      {!selectedProposalId ? (
        <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 16 }}>{isZh ? '請先在上方選擇方法論提案' : 'Please select a methodology proposal from the top bar'}</div>
      ) : (
        <div style={{ width: '100%', background: '#fff', borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', border: '1px solid #e8e8e8', padding: '28px 32px' }}>
          {/* 進度列 */}
          <div style={{ padding: '12px 16px', marginBottom: 16, borderRadius: 8, background: totalCount > 0 && builtCount >= totalCount ? '#f6ffed' : '#f8f9fa', border: `1px solid ${totalCount > 0 && builtCount >= totalCount ? '#b7eb8f' : '#e8e8e8'}` }}>
            <div style={{ fontSize: 14, fontWeight: 600 }}>
              {totalCount > 0
                ? (builtCount >= totalCount
                    ? <span style={{ color: '#52c41a' }}>✅ {isZh ? '所有必要參數已建立' : 'All required parameters created'}</span>
                    : <span>{isZh ? `B.13 事前參數：已建立 ${builtCount} / 共 ${totalCount} 個　尚餘 ${totalCount - builtCount} 個未建立` : `B.13 parameters: ${builtCount} / ${totalCount} created, ${totalCount - builtCount} remaining`}</span>)
                : <span style={{ color: '#999' }}>{isZh ? 'B.10~B.13 尚無參數資料，可手動新增' : 'No B.10~B.13 parameters, add manually'}</span>}
            </div>
          </div>

          {/* 操作列 */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{isZh ? '事前參數清單' : 'Parameters not monitored'}</div>
            {canEdit && <button className="btn-primary" onClick={() => openModal()}>{t('common.create')}</button>}
          </div>

          {/* DataTable */}
          {parameters.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#999', fontSize: 14 }}>{isZh ? '尚無參數' : 'No parameters yet.'}</div>
          ) : (
            <div className="data-table-container"><table className="data-table"><thead className="table-header-dark-green"><tr>
              <th style={{ width: '4%' }}>#</th>
              <th style={{ width: '6%' }}>{isZh ? '來源' : 'Src'}</th>
              <th style={{ width: '14%' }}>{isZh ? '參數' : 'Parameter'}</th>
              <th style={{ width: '16%' }}>{isZh ? '說明' : 'Description'}</th>
              <th style={{ width: '8%' }}>{isZh ? '單位' : 'Unit'}</th>
              <th style={{ width: '14%' }}>{isZh ? '用途' : 'Purpose'}</th>
              <th style={{ width: '8%' }}>{isZh ? '來源' : 'Source'}</th>
              <th style={{ width: '20%' }}>{isZh ? '套用值' : 'Values'}</th>
              <th style={{ width: '10%' }}>{t('common.actions')}</th>
            </tr></thead><tbody>
              {parameters.map((p, idx) => (
                <tr key={p.id}>
                  <td>{idx + 1}</td>
                  <td><span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: p.b13_ref ? '#e6f7ff' : '#f5f5f5', color: p.b13_ref ? '#1890ff' : '#999' }}>{p.b13_ref ? 'B.13' : isZh ? '手動' : 'Manual'}</span></td>
                  <td>{p.data_parameter && (() => { try { return <InlineMath math={p.data_parameter} />; } catch { return <span>{p.data_parameter}</span>; } })()}</td>
                  <td style={{ fontSize: 13 }}>{p.description || '—'}</td>
                  <td>{p.data_unit && (() => { try { return <InlineMath math={p.data_unit} />; } catch { return <span>{p.data_unit}</span>; } })()}</td>
                  <td style={{ fontSize: 12 }}>
                    {p.purpose_baseline && <span style={{ display: 'inline-block', padding: '1px 6px', background: '#f0f7ff', borderRadius: 3, marginRight: 4, marginBottom: 2 }}>Baseline</span>}
                    {p.purpose_project && <span style={{ display: 'inline-block', padding: '1px 6px', background: '#f6ffed', borderRadius: 3, marginRight: 4, marginBottom: 2 }}>Project</span>}
                    {p.purpose_leakage && <span style={{ display: 'inline-block', padding: '1px 6px', background: '#fff7e6', borderRadius: 3, marginBottom: 2 }}>Leakage</span>}
                  </td>
                  <td style={{ fontSize: 12 }}>{p.source === 'measured' ? (isZh ? '量測' : 'Measured') : p.source === 'other' ? (isZh ? '其他' : 'Other') : '—'}</td>
                  <td style={{ fontSize: 12 }} dangerouslySetInnerHTML={{ __html: p.values_applied || '—' }} />
                  <td className="actions">{canEdit && <>
                    <button className="btn-edit" onClick={() => openModal(idx)} style={{ marginRight: 8 }}>{t('common.edit')}</button>
                    <button className="btn-delete" onClick={() => handleDeleteParam(idx)}>{t('common.delete')}</button>
                  </>}</td>
                </tr>
              ))}
            </tbody></table></div>
          )}

          {/* 儲存 */}
          {canEdit && (
            <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 20, borderTop: '1px solid #f0f0f0', marginTop: 16 }}>
              <button className="btn-primary" onClick={handleSave} disabled={loading} style={{ padding: '8px 32px' }}>{loading ? (isZh ? '儲存中...' : 'Saving...') : t('common.save')}</button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: '92vw', width: 1200 }}>
            <div className="modal-header">
              <h2>{editingIndex !== null ? (isZh ? '編輯參數' : 'Edit Parameter') : (isZh ? '新增參數' : 'Add Parameter')}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {/* B.13 下拉選取（新增模式） */}
              {editingIndex === null && availableParams.length > 0 && (
                <div style={{ padding: '12px 16px', background: '#f0f7ff', borderRadius: 8, border: '1px solid #bde0ff', marginBottom: 20 }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>
                    {isZh ? `從 B.13 選取參數（${availableParams.length} 個尚未建立）` : `Select from B.13 (${availableParams.length} remaining)`}
                  </label>
                  <select onChange={e => { if (e.target.value) handleB13Select(e.target.value); }} style={{ width: '100%', padding: '8px 12px', fontSize: 14, border: '1px solid #d0d0d0', borderRadius: 6 }}>
                    <option value="">{isZh ? '不選擇（手動輸入）' : 'Skip (manual input)'}</option>
                    {availableParams.map((p, i) => (
                      <option key={i} value={p.symbol}>{p.symbol} — {p.source_section} {p.source_label} ({p.para_type === 249 ? 'Ex ante' : 'All'})</option>
                    ))}
                  </select>
                </div>
              )}

              {/* 來源方程式參考（B.13 帶入時顯示） */}
              {modalParam.b13_ref && (
                <div style={{ padding: '10px 16px', background: '#f8faff', borderRadius: 6, border: '1px solid #d0e4ff', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#4A90E2', fontWeight: 600, marginBottom: 4 }}>
                    {isZh ? '來源方程式' : 'Source equation'}: {modalParam.b13_ref.source_section} — {modalParam.b13_ref.source_label}
                  </div>
                  {modalParam.b13_ref.equation_latex && (() => { try { return <BlockMath math={modalParam.b13_ref.equation_latex} />; } catch { return null; } })()}
                </div>
              )}

              {/* 表單欄位 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>
                {/* Data/parameter */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Data/parameter *</label>
                  <input value={modalParam.data_parameter} onChange={e => setModalParam(p => ({ ...p, data_parameter: e.target.value }))}
                    placeholder="e.g. EF_{i,y}" style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 14, boxSizing: 'border-box' }} />
                  {modalParam.data_parameter && <div style={{ marginTop: 4 }}>{(() => { try { return <InlineMath math={modalParam.data_parameter} />; } catch { return null; } })()}</div>}
                </div>
                {/* Data unit */}
                <div>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Data unit</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input value={modalParam.data_unit} onChange={e => setModalParam(p => ({ ...p, data_unit: e.target.value }))}
                      placeholder="LaTeX unit" style={{ flex: 1, padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontFamily: 'monospace', fontSize: 13, boxSizing: 'border-box' }} />
                    <select value="" onChange={e => { if (e.target.value) setModalParam(p => ({ ...p, data_unit: e.target.value })); }}
                      style={{ padding: '4px 8px', fontSize: 12, border: '1px solid #d0d0d0', borderRadius: 4 }}>
                      <option value="">{isZh ? '常用' : 'Units'}</option>
                      {COMMON_UNITS.map((u, i) => <option key={i} value={u.latex}>{u.label}</option>)}
                    </select>
                  </div>
                  {modalParam.data_unit && <div style={{ marginTop: 4 }}>{(() => { try { return <InlineMath math={modalParam.data_unit} />; } catch { return null; } })()}</div>}
                </div>
                {/* Description */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Description *</label>
                  <input value={modalParam.description} onChange={e => setModalParam(p => ({ ...p, description: e.target.value }))}
                    placeholder={isZh ? '參數描述' : 'Parameter description'} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                {/* Equations referred */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Equations referred</label>
                  <input value={modalParam.equations_referred} onChange={e => setModalParam(p => ({ ...p, equations_referred: e.target.value }))}
                    placeholder={isZh ? '引用的方程式' : 'Referenced equations'} style={{ width: '100%', padding: '8px 10px', border: '1px solid #d0d0d0', borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }} />
                </div>
                {/* Purpose of data */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Purpose of data</label>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={modalParam.purpose_baseline} onChange={e => setModalParam(p => ({ ...p, purpose_baseline: e.target.checked }))} /> Baseline emissions
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={modalParam.purpose_project} onChange={e => setModalParam(p => ({ ...p, purpose_project: e.target.checked }))} /> Project emissions
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="checkbox" checked={modalParam.purpose_leakage} onChange={e => setModalParam(p => ({ ...p, purpose_leakage: e.target.checked }))} /> Leakage emissions
                    </label>
                  </div>
                </div>
                {/* Values applied */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Value(s) applied</label>
                  <RichTextEditor value={modalParam.values_applied} onChange={v => setModalParam(p => ({ ...p, values_applied: v }))} placeholder={isZh ? '套用的數值說明...' : 'Values applied...'} minHeight="80px" />
                </div>
                {/* Source of data */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 6 }}>Source of data</label>
                  <div style={{ display: 'flex', gap: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="radio" name="source" checked={modalParam.source === 'measured'} onChange={() => setModalParam(p => ({ ...p, source: 'measured' }))} /> {isZh ? '量測 [Measured]' : 'Measured'}
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                      <input type="radio" name="source" checked={modalParam.source === 'other'} onChange={() => setModalParam(p => ({ ...p, source: 'other' }))} /> {isZh ? '其他來源 [Other sources]' : 'Other sources'}
                    </label>
                  </div>
                </div>
                {/* Measurement methods */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>
                    {modalParam.source === 'measured' ? (isZh ? '量測方法與程序' : 'Measurement methods and procedures') : (isZh ? '資料選擇與理由說明' : 'Choice of data and justification')}
                  </label>
                  <RichTextEditor value={modalParam.measurement_methods} onChange={v => setModalParam(p => ({ ...p, measurement_methods: v }))} placeholder={isZh ? '說明...' : 'Description...'} minHeight="80px" />
                </div>
                {/* Treatment of uncertainty */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Treatment of uncertainty</label>
                  <RichTextEditor value={modalParam.treatment_of_uncertainty} onChange={v => setModalParam(p => ({ ...p, treatment_of_uncertainty: v }))} placeholder={isZh ? '不確定性處理說明...' : 'Describe uncertainty treatment...'} minHeight="80px" />
                </div>
                {/* Additional comments */}
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ fontWeight: 600, fontSize: 13, display: 'block', marginBottom: 4 }}>Additional comments</label>
                  <RichTextEditor value={modalParam.additional_comments} onChange={v => setModalParam(p => ({ ...p, additional_comments: v }))} placeholder={isZh ? '補充說明（選填）...' : 'Optional comments...'} minHeight="60px" />
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setShowModal(false)}>{t('common.cancel')}</button>
              {editingIndex === null && (
                <button className="btn-secondary" onClick={() => handleSaveParam(true)} style={{ marginLeft: 8 }}>
                  {isZh ? '儲存並繼續新增' : 'Save & Add Next'}
                </button>
              )}
              <button className="btn-primary" onClick={() => handleSaveParam(false)} style={{ marginLeft: 8 }}>
                {editingIndex !== null ? t('common.save') : (isZh ? '儲存並關閉' : 'Save & Close')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NsectionB14Page;
